import { EventCardSkeleton, EventsGrid } from '@/components';
import {
    ReportStatus,
    ReportType,
    type ReportEvent,
    type ReportPipelineStatus,
    type ReportWithEvents,
} from '@/types/report';
import { type ReportGroupByMode } from '../constants';
import type { ReportEventReordering } from '../hooks/useReportEventOrdering';
import type { ReportPromptReordering } from '../hooks/useReportPromptOrdering';
import { ProcedureNoSourcesEmptyState } from './ProcedureNoSourcesEmptyState';
import { ReportEventsEmptyState } from './ReportEventsEmptyState';
import { ReportEventsFilteredEmptyState } from './ReportEventsFilteredEmptyState';
import { ReportEventsList } from './ReportEventsList';

const REPORT_EVENTS_LOADING_SKELETON_COUNT = 6;

interface ReportEventsSectionProps {
    isInitialLoading: boolean;
    reportId: string;
    report: ReportWithEvents | undefined;
    isProcedureReport: boolean;
    pipelineStatus: ReportPipelineStatus | undefined;
    visibleEvents: ReportEvent[];
    groupBy: ReportGroupByMode;
    identitySelectionKey: string;
    isMultiSelectMode: boolean;
    selectedIds: Set<string>;
    onToggleSelect: (eventId: string) => void;
    onEditEvent: (eventId: string) => void;
    onRemoveEvent: (eventId: string) => void;
    onExportEvent: (event: ReportEvent) => void;
    exportBusy: boolean;
    onAddEvents: () => void;
    onAddSource: () => void;
    reordering: ReportEventReordering;
    promptReordering: ReportPromptReordering;
}

function LoadingSkeletonGrid() {
    return (
        <EventsGrid>
            {Array.from({ length: REPORT_EVENTS_LOADING_SKELETON_COUNT }).map(
                (_, index) => (
                    <EventCardSkeleton key={index} />
                ),
            )}
        </EventsGrid>
    );
}

export function ReportEventsSection({
    isInitialLoading,
    reportId,
    report,
    isProcedureReport,
    pipelineStatus,
    visibleEvents,
    groupBy,
    identitySelectionKey,
    isMultiSelectMode,
    selectedIds,
    onToggleSelect,
    onEditEvent,
    onRemoveEvent,
    onExportEvent,
    exportBusy,
    onAddEvents,
    onAddSource,
    reordering,
    promptReordering,
}: ReportEventsSectionProps) {
    if (isInitialLoading) {
        return (
            <div className="py-4">
                <LoadingSkeletonGrid />
            </div>
        );
    }

    // `report.events` already had the confidence filter applied by the server,
    // so an empty list only means an empty report when nothing was filtered out.
    const hiddenEventCount =
        (report?.totalEventCount ?? 0) - visibleEvents.length;

    const showFilteredEmptyState =
        report !== undefined &&
        visibleEvents.length === 0 &&
        hiddenEventCount > 0;

    const showNormalEmptyState =
        report?.type === ReportType.NORMAL &&
        report.events.length === 0 &&
        !showFilteredEmptyState;

    const showProcedureNoSourcesEmptyState =
        isProcedureReport &&
        report !== undefined &&
        report.events.length === 0 &&
        !showFilteredEmptyState &&
        pipelineStatus !== undefined &&
        pipelineStatus.sources.length === 0;

    const showProcedureEventsGeneratingSkeleton =
        isProcedureReport &&
        report !== undefined &&
        report.events.length === 0 &&
        !showFilteredEmptyState &&
        report.status !== ReportStatus.COMPLETED &&
        report.status !== ReportStatus.ERROR &&
        !showProcedureNoSourcesEmptyState;

    return (
        <div className="py-4">
            {showFilteredEmptyState ? (
                <ReportEventsFilteredEmptyState
                    hiddenEventCount={hiddenEventCount}
                />
            ) : showNormalEmptyState ? (
                <ReportEventsEmptyState onAddEvents={onAddEvents} />
            ) : showProcedureNoSourcesEmptyState ? (
                <ProcedureNoSourcesEmptyState onAddSource={onAddSource} />
            ) : showProcedureEventsGeneratingSkeleton ? (
                <LoadingSkeletonGrid />
            ) : (
                <ReportEventsList
                    reportId={reportId}
                    events={visibleEvents}
                    groupBy={groupBy}
                    identitySelectionKey={identitySelectionKey}
                    onEdit={onEditEvent}
                    onDelete={onRemoveEvent}
                    onExport={onExportEvent}
                    exportBusy={exportBusy}
                    isMultiSelectMode={isMultiSelectMode}
                    selectedIds={selectedIds}
                    onToggleSelect={onToggleSelect}
                    reordering={reordering}
                    promptReordering={promptReordering}
                />
            )}
        </div>
    );
}
